//@import dependencies
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import {AiFillEdit, AiOutlineUserAdd} from 'react-icons/ai'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {HiUserRemove} from 'react-icons/hi'
//@end

//@import components
import DataTableComponent from 'app_components/commons/dataTable'
import AddNewCompanyFormModuleLayout from './addNewCompanyUserFormModuleLayout'
//@end

// @import hooks
import { useColors } from 'app_hooks/useColors'
// @end

// @INFO Import types
import { IUserListItem } from 'app_types/admin/user/userTypes'
import { IPagination } from 'app_types/pagination/paginationTypes'
import { ICompanyListItem } from 'app_types/admin/company/companyTypes'
// @end

// @INFO Import services
import UserService from 'app_services/user/userService'
// @end

// @INFO Import Styles
import './../modalCompanyUsersModule.scss'
import { IUserData } from 'app_types/userTypes';
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'

// @ADD Component types
interface IModalCompanyUsersModuleLayoutProps {
  open: boolean
  processing: boolean
  loading: boolean
  handleClose: (data: any) => void
  onPageChange: (page: number) => void
  refreshList: (data?: any) => void
  errors: Array<any>
  pagination: IPagination,
  users: IUserListItem[]
  usersAvailable: IUserListItem[]
  company: ICompanyListItem
}

const ModalCompanyUsersModuleLayout: React.FC<IModalCompanyUsersModuleLayoutProps> = (props) => {

  const userService: UserService = new UserService()

  const { t } = useTranslation()
  const [colors] = useColors()

  const [processing, setProcessing] = useState<boolean>(false)
  const [addNewUserStatus, setAddNewUserStatus] = useState<boolean>(false)
  const [editData, setEditData] = useState<IUserData | undefined>()

  const { open } = props

  const handleCloseModal = (data: any) => {
    props.handleClose(data)
  }

  const handleAddNewUser = (data?: any) => {
    if (!addNewUserStatus) {
      setAddNewUserStatus(true)
    } else if (addNewUserStatus && data) {
      if (data?.status === 'saved') {
        props.refreshList({
          page: 1,
          searchUsersAvailable: true
        })
      }
      setEditData(undefined)
      setAddNewUserStatus(false)
    }
  }

  const removeUser = async (user: IUserListItem) => {
    setProcessing(true)

    const userResponse = await userService.insertOrUpdate(
      {
        id: user._id,
        company: undefined,
      },
      `update/${user._id}`
    )
    if (userResponse) {
      setProcessing(false)
      props.refreshList({
        page: 1,
        searchUsersAvailable: true
      })
    }
    setProcessing(false)
  }

  const handleEditClick = (data: any) => {
    console.log('On edit click: ', data)
    setEditData(data)
    setAddNewUserStatus(true)
  }

  return(
    <div className=''>
      <Modal
        show={open}
        onHide={() => handleCloseModal({ status: false })}
        className="modal_company_users-component"
        size='lg'
      >
        <Modal.Header>
          <Modal.Title>
            {t('modules.modal_company_users.title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {props.loading ? (
            <div className="loading">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            </div>
          ) : (
            <>
              {props.errors.length > 0 && (
                <div className="alert alert-warning">
                  <ul>
                    {props.errors.map((error) => (
                      <li>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {addNewUserStatus && userService.can('permission:companies_create', 'module:companies') && (
                <AddNewCompanyFormModuleLayout
                  users={props.usersAvailable}
                  handleClose={handleAddNewUser}
                  company={props.company}
                  editData={editData}
                />
              )}
              <DataTableComponent
                data={props.users?.map((user) => ({ ...user, admin_company_type: user.admin_company ? 'Super usuario' : 'Estandar' }))}
                headerKeys={[
                  '',
                  `${t('components.dataTable.headers.first_name')}`,
                  `${t('components.dataTable.headers.last_name')}`,
                  `${t('components.dataTable.headers.user_name')}`,
                  `Tipo de usuario`,
                ]}
                keys={['profile.avatar', 'profile.first_name', 'profile.last_name', 'username', 'admin_company_type']}
                pagination={props.pagination}
                detailsKey={[
                  { name: `${t('components.dataTable.details.email')}`, key: 'profile.email' },
                ]}
                onPageChange={props.onPageChange}
                rowButtons={(user: IUserListItem) => {
                  return (
                    <div className='d-flex gap-2 align-items-center'>
                      {userService.can('permission:companies_create', 'module:companies') && (
                        <TooltipComponent
                          text='Editar colaborador'
                        >
                          <AiFillEdit
                            size={'1.5rem'}
                            color={colors.primary}
                            onClick={() => handleEditClick(user)}
                            style={{ cursor: 'pointer' }}
                          />
                        </TooltipComponent>
                      )}
                      {userService.can('permission:companies_create', 'module:companies') && (
                        <OverlayTrigger
                          trigger={['hover', 'focus']}
                          placement={'left'}
                          overlay={
                            <Tooltip id='toolip-circle'>
                              {t('modules.modal_company_users.actions.remove_user')}
                            </Tooltip>
                          }>
                          <button
                            className='btn btn-link btn-sm'
                            onClick={() => removeUser(user)}
                            disabled={processing}
                          >
                            <HiUserRemove size={'1.5rem'} color={colors.primary} />
                          </button>
                        </OverlayTrigger>
                      )}
                    </div>
                  )
                }}
                headerButtons={() => {
                  return (
                    <>
                      {!addNewUserStatus && userService.can('permission:companies_create', 'module:companies') && (
                        <OverlayTrigger
                          trigger={['hover', 'focus']}
                          placement={'left'}
                          overlay={
                            <Tooltip id='toolip-circle'>
                              {t('modules.modal_company_users.actions.add_new')}
                            </Tooltip>
                          }>
                          <button
                            className='btn btn-link btn-sm'
                            onClick={() => handleAddNewUser()}
                            disabled={processing}
                          >
                            <AiOutlineUserAdd size={'1.5rem'} color={colors.primary} />
                          </button>
                        </OverlayTrigger>
                      )}
                    </>
                  )
                }}
                onModal={null}
              />
            </>
          )}
        </Modal.Body>
      </Modal>

    </div>
  )
}

export default ModalCompanyUsersModuleLayout
